import React, { useState, useEffect } from 'react';
import { Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { goldenFastAPI } from '../../features/vps/vpsGoldenFastAPI';
import './style.css';

const VpsAddonsSelector = ({
  productDetail,
  billingCycle = 'annually',
  selectedAddons,
  onAddonsChange,
  existingAddons = [],
  onValidationChange
}) => {
  const [addons, setAddons] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedOperatingSystem, setSelectedOperatingSystem] = useState('');
  const [availableControlPanels, setAvailableControlPanels] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubCategories, setExpandedSubCategories] = useState(new Set());
  const [validationTimeout, setValidationTimeout] = useState(null);

  // Toggle accordion for a specific category
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
        // Collapse all sub-categories when main category is collapsed
        setExpandedSubCategories(new Set());
      } else {
        newSet.add(categoryName);
        // Auto-expand first sub-category when main category is expanded
        if (addons[categoryName]) {
          const firstSubCategory = Object.keys(addons[categoryName])[0];
          if (firstSubCategory) {
            setExpandedSubCategories(new Set([firstSubCategory]));
          }
        }
      }
      return newSet;
    });
  };

  // Toggle accordion for a specific sub-category
  const toggleSubCategory = (subCategoryName) => {
    setExpandedSubCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subCategoryName)) {
        newSet.delete(subCategoryName);
      } else {
        newSet.add(subCategoryName);
      }
      return newSet;
    });
  };

  // Initialize selected addons with existing addons from cart
  useEffect(() => {
    if (existingAddons.length > 0 && selectedAddons.length === 0) {
      onAddonsChange(existingAddons);
    }
  }, [existingAddons, selectedAddons.length, onAddonsChange]);

  // Update existing addons when they change
  useEffect(() => {
    // Force re-render when existingAddons change
    if (existingAddons.length > 0) {
      // This will trigger the component to re-render and show updated existing addons
    }
  }, [existingAddons]);

  // Process and group addons when productDetail changes
  useEffect(() => {
    if (productDetail && productDetail.pid) {
      // Check if addons are already in productDetail
      if (productDetail.addons && Array.isArray(productDetail.addons)) {
        processAddonsData(productDetail.addons);
      } else {
        loadAddonsFromAPI(productDetail.pid);
      }
    } else {
      setAddons({});
    }
  }, [productDetail]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  // Validate whenever selectedAddons changes - but only when actually changed
  useEffect(() => {
    if (productDetail && onValidationChange && selectedAddons.length > 0) {
      // Only validate once when addons are added - use a ref to prevent multiple validations
      const timeoutId = setTimeout(() => {
        validateRequiredConfigurationsWithAddons(selectedAddons);
      }, 600); // Increased timeout to prevent rapid validation
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedAddons.length, productDetail, onValidationChange]); // Only depend on length, not the entire array

  const loadAddonsFromAPI = async (pid) => {
    setLoading(true);
    try {
      const response = await goldenFastAPI.loadProductAddons(pid);

      if (response.status && response.data && response.data.length > 0) {
        const productData = response.data[0];

        if (productData.addons && Array.isArray(productData.addons)) {
          processAddonsData(productData.addons);
        } else {
          setAddons({});
        }
      } else {
        setAddons({});
      }
    } catch (error) {
      console.error('❌ Error loading addons from API:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        pid: pid
      });
      setAddons({});
    } finally {
      setLoading(false);
    }
  };

  const processAddonsData = (rawAddons) => {
    if (!Array.isArray(rawAddons) || rawAddons.length === 0) {
      setAddons({});
      return;
    }

    const filteredAddons = goldenFastAPI.filterAddons(rawAddons);
    if (filteredAddons.length === 0) {
      setAddons({});
      return;
    }

    // Function to categorize addon based on name and description
    const categorizeAddon = (addon) => {
      const name = (addon.name || '').toLowerCase();
      const description = (addon.description || '').toLowerCase();

      // Hardware category
      if (name.includes('ram') || name.includes('memory') || name.includes('cpu') ||
        name.includes('processor') || name.includes('disk') || name.includes('ssd') ||
        name.includes('hdd') || name.includes('storage') || name.includes('bandwidth') ||
        name.includes('traffic') || name.includes('port') || name.includes('nic') ||
        name.includes('gpu') || name.includes('graphics') || name.includes('raid') ||
        description.includes('hardware') || description.includes('ram') ||
        description.includes('cpu') || description.includes('storage') ||
        description.includes('memory') || description.includes('disk')) {
        return 'Hardware';
      }

      // Software category
      if (name.includes('os') || name.includes('operating system') || name.includes('windows') ||
        name.includes('linux') || name.includes('centos') || name.includes('ubuntu') ||
        name.includes('debian') || name.includes('cpanel') || name.includes('plesk') ||
        name.includes('directadmin') || name.includes('control panel') ||
        name.includes('software') || name.includes('application') ||
        name.includes('database') || name.includes('mysql') || name.includes('postgresql') ||
        name.includes('web server') || name.includes('apache') || name.includes('nginx') ||
        description.includes('operating system') || description.includes('control panel') ||
        description.includes('software') || description.includes('database')) {
        return 'Software';
      }

      // Network category
      if (name.includes('ip') || name.includes('ddos') || name.includes('firewall') ||
        name.includes('vpn') || name.includes('proxy') || name.includes('cdn') ||
        name.includes('load balancer') || name.includes('network') ||
        name.includes('ssl') || name.includes('certificate') || name.includes('domain') ||
        name.includes('dns') || name.includes('mail') || name.includes('email') ||
        description.includes('network') || description.includes('ip') ||
        description.includes('ddos protection') || description.includes('ssl') ||
        description.includes('security')) {
        return 'Network';
      }

      // Backup category
      if (name.includes('backup') || name.includes('snapshot') || name.includes('restore') ||
        name.includes('recovery') || name.includes('archive') ||
        description.includes('backup') || description.includes('snapshot') ||
        description.includes('recovery')) {
        return 'Backup';
      }

      // Support category
      if (name.includes('support') || name.includes('maintenance') || name.includes('monitoring') ||
        name.includes('management') || name.includes('admin') || name.includes('helpdesk') ||
        description.includes('support') || description.includes('maintenance') ||
        description.includes('monitoring')) {
        return 'Support';
      }

      // Cross Connect category
      if (name.includes('cross connect') || name.includes('cross-connect') ||
        name.includes('interconnect') || name.includes('peering') ||
        description.includes('cross connect') || description.includes('interconnect')) {
        return 'Cross Connect';
      }

      // PPh23 and tax related
      if (name.includes('pph') || name.includes('tax') || name.includes('potong') ||
        name.includes('pajak') || name.includes('faktur') ||
        description.includes('pph') || description.includes('tax') ||
        description.includes('pajak')) {
        return 'Others';
      }

      // Default to Others if no match
      return 'Others';
    };

    // Function to determine input_name based on addon properties
    const determineInputName = (addon) => {
      const name = (addon.name || '').toLowerCase();
      const category = addon.category;

      // If already has a good input_name, use it
      if (addon.input_name && addon.input_name !== 'others' && addon.input_name !== 'Others') {
        return addon.input_name;
      }

      // Hardware input names
      if (category === 'Hardware') {
        if (name.includes('ram') || name.includes('memory')) return 'Memory/RAM';
        if (name.includes('cpu') || name.includes('processor')) return 'Processor/CPU';
        if (name.includes('disk') || name.includes('ssd') || name.includes('hdd') || name.includes('storage')) return 'Storage';
        if (name.includes('bandwidth') || name.includes('traffic')) return 'Bandwidth';
        return 'Hardware';
      }

      // Software input names
      if (category === 'Software') {
        if (name.includes('os') || name.includes('operating system') || name.includes('ubuntu') || name.includes('debian') || name.includes('centos') || name.includes('windows') || name.includes('fedora') || name.includes('almalinux') || name.includes('rocky')) return 'Operating System';
        if (name.includes('cpanel') || name.includes('plesk') || name.includes('directadmin')) return 'Control Panel';
        if (name.includes('database')) return 'Database';
        if (name.includes('web server')) return 'Web Server';
        return 'Software';
      }

      // Network input names
      if (category === 'Network') {
        if (name.includes('ip')) return 'IP Address';
        if (name.includes('ssl') || name.includes('certificate')) return 'SSL Certificate';
        if (name.includes('ddos')) return 'DDoS Protection';
        if (name.includes('firewall')) return 'Firewall';
        if (name.includes('vpn')) return 'VPN';
        if (name.includes('mail') || name.includes('email')) return 'Email Service';
        return 'Network';
      }

      // Support input names
      if (category === 'Support') {
        if (name.includes('support')) return 'Support Level';
        if (name.includes('monitoring')) return 'Monitoring';
        if (name.includes('management')) return 'Management';
        return 'Support';
      }

      // Default to addon name
      return addon.name;
    };

    // Process and categorize addons
    const categorizedAddons = filteredAddons.map(addon => {
      // If category is 'others' or undefined, try to categorize based on name/description
      if (!addon.category || addon.category === 'others' || addon.category === 'Others') {
        const detectedCategory = categorizeAddon(addon);
        const detectedInputName = determineInputName({ ...addon, category: detectedCategory });
        return {
          ...addon,
          category: detectedCategory,
          input_name: detectedInputName
        };
      } else {
        // Even if category is already set, try to improve input_name
        const improvedInputName = determineInputName(addon);
        if (improvedInputName !== addon.input_name) {
        }
        return {
          ...addon,
          input_name: improvedInputName
        };
      }
    });

    // Remove duplicates based on name and value before grouping
    const deduplicatedAddons = categorizedAddons.reduce((unique, addon) => {
      // Create a unique key based on name and pricing
      const key = `${addon.name}-${addon.annually}-${addon.monthly}-${addon.quarterly}-${addon.semiannually}`;
      
      // Check if this addon already exists
      const existingIndex = unique.findIndex(existing => {
        const existingKey = `${existing.name}-${existing.annually}-${existing.monthly}-${existing.quarterly}-${existing.semiannually}`;
        return existingKey === key;
      });
      
      if (existingIndex === -1) {
        // Addon doesn't exist, add it
        unique.push(addon);
      } else {
        // Addon exists, keep the one with better data (more complete info)
        const existing = unique[existingIndex];
        if (addon.description && addon.description.length > (existing.description || '').length) {
          unique[existingIndex] = addon; // Replace with better version
        }
      }
      
      return unique;
    }, []);

    // Group addons by category and input_name
    const groupedByCategory = deduplicatedAddons.reduce((groups, addon) => {
      // Normalize category names
      let category = addon.category || 'others';

      // Map API categories to display categories
      const categoryMapping = {
        'Software': 'Software',
        'Network': 'Network',
        'Backup': 'Backup',
        'Support-Level': 'Support',
        'Others': 'Others',
        'others': 'Others',
        'Hardware': 'Hardware',
        'Cross Connect': 'Cross Connect'
      };

      category = categoryMapping[category] || category;

      if (!groups[category]) groups[category] = [];
      groups[category].push(addon);
      return groups;
    }, {});


    const groupedWithInputs = Object.keys(groupedByCategory).reduce((result, category) => {
      const categoryAddons = groupedByCategory[category];

      // Special handling for "Others" category
      if (category === 'Others') {
        result[category] = categoryAddons.reduce((inputGroups, addon) => {
          let inputName = addon.input_name || addon.name;

          // Special case: if input_name is "others", group them under "Lainnya"
          if (inputName === 'others' || inputName === 'Others') {
            inputName = 'Lainnya';
          }

          if (!inputGroups[inputName]) inputGroups[inputName] = [];
          inputGroups[inputName].push(addon);
          return inputGroups;
        }, {});
      } else {
        // Special handling for Backup category - group similar backup options
      if (category === 'Backup') {
        result[category] = categoryAddons.reduce((inputGroups, addon) => {
          const name = addon.name.toLowerCase();
          
          // Group FTP Backup options together
          if (name.includes('ftp backup') || name.includes('ftp backup / nas')) {
            const inputName = 'FTP Backup / NAS';
            if (!inputGroups[inputName]) inputGroups[inputName] = [];
            inputGroups[inputName].push(addon);
          }
          // Group other backup types
          else if (name.includes('backup')) {
            const inputName = 'Backup';
            if (!inputGroups[inputName]) inputGroups[inputName] = [];
            inputGroups[inputName].push(addon);
          }
          // Default grouping
          else {
            const inputName = addon.input_name || addon.name;
            if (!inputGroups[inputName]) inputGroups[inputName] = [];
            inputGroups[inputName].push(addon);
          }
          
          return inputGroups;
        }, {});
      } else {
        // Normal grouping for other categories
        result[category] = categoryAddons.reduce((inputGroups, addon) => {
          const inputName = addon.input_name || addon.name;
          if (!inputGroups[inputName]) inputGroups[inputName] = [];
          inputGroups[inputName].push(addon);
          return inputGroups;
        }, {});
      }
      }

      return result;
    }, {});

    // Sort categories by priority
    const categoryOrder = ['Hardware', 'Software', 'Network', 'Cross Connect', 'Backup', 'Support', 'Others'];
    const sortedCategories = {};

    categoryOrder.forEach(category => {
      if (groupedWithInputs[category]) {
        sortedCategories[category] = groupedWithInputs[category];
      }
    });

    // Add any remaining categories not in the predefined order
    Object.keys(groupedWithInputs).forEach(category => {
      if (!categoryOrder.includes(category)) {
        sortedCategories[category] = groupedWithInputs[category];
      }
    });

    // Log addon count by category
    Object.keys(sortedCategories).forEach(category => {
      const categoryAddons = sortedCategories[category];
      // Log sub-categories for debugging
      Object.keys(categoryAddons).forEach(subCategory => {
        categoryAddons[subCategory].forEach(addon => {
        });
      });
    });

    setAddons(sortedCategories);
  };

  // Handle Operating System change
  useEffect(() => {
    if (selectedOperatingSystem) {
      updateAvailableControlPanels(selectedOperatingSystem);
    }
  }, [selectedOperatingSystem]);

  const updateAvailableControlPanels = (osName) => {
    const relatedAddons = goldenFastAPI.getOSRelatedAddons(osName);

    // Find control panel addons that match the OS
    const controlPanelAddons = addons?.Software?.['Control Panel'] || [];
    const available = controlPanelAddons.filter(addon =>
      relatedAddons.some(related =>
        addon.name.toLowerCase().includes(related.toLowerCase())
      )
    );

    setAvailableControlPanels(available);
  };

  const handleAddonChange = (addon, isSelected) => {
    let updatedAddons = [...selectedAddons];

    // Determine if this category allows multiple selections
    const allowMultiple = (addon.input_name || addon.name).toLowerCase().includes('additional') ||
      (addon.input_name || addon.name).toLowerCase().includes('extra') ||
      (addon.input_name || addon.name).toLowerCase().includes('backup') ||
      addon.input_name === 'others' ||
      addon.input_name === 'Others' ||
      addon.input_name === 'Lainnya';

    if (isSelected) {
      if (allowMultiple) {
        // For multiple selection categories, check if addon already exists to prevent duplication
        const existingAddonIndex = updatedAddons.findIndex(selected => selected.id === addon.id);
        
        if (existingAddonIndex === -1) {
          // Check for duplicates based on name and value
          const isDuplicate = updatedAddons.some(existing => 
            existing.name === addon.name && 
            existing[billingCycle] === addon[billingCycle]
          );
          
          if (!isDuplicate) {
            // Addon doesn't exist and not duplicate, add it
            const addonWithPricing = {
              ...addon,
              // Ensure input_name is set correctly for OS addons
              input_name: addon.input_name === 'Operating System' || addon.name.toLowerCase().includes('operating system') || addon.name.toLowerCase().includes('fedora') || addon.name.toLowerCase().includes('ubuntu') || addon.name.toLowerCase().includes('centos') || addon.name.toLowerCase().includes('debian') || addon.name.toLowerCase().includes('windows') || addon.name.toLowerCase().includes('almalinux') || addon.name.toLowerCase().includes('rocky') || addon.name.toLowerCase().includes('freebsd') ? 'Operating System' : addon.input_name || addon.name,
              // Add billing cycle specific pricing
              [billingCycle]: addon[billingCycle] || '0.00',
              // Add setup fee
              [billingCycle[0] + 'setupfee']: addon[billingCycle[0] + 'setupfee'] || '0.00',
              // Add billing cycle info
              billingcycle: addon.billingcycle || 'recurring',
              // Add price for cart calculation
              price: parseFloat(addon[billingCycle] || 0),
              setupfee: parseFloat(addon[billingCycle[0] + 'setupfee'] || 0)
            };

            updatedAddons.push(addonWithPricing);
          }
        }
        // If addon already exists, do nothing (prevent duplication)
      } else {
        // For single selection categories, remove any existing addon with the same input_name (radio button behavior)
        updatedAddons = updatedAddons.filter(selected => selected.input_name !== addon.input_name);

        // Add addon with proper pricing structure
        const addonWithPricing = {
          ...addon,
          // Ensure input_name is set correctly for OS addons
          input_name: addon.input_name === 'Operating System' || addon.name.toLowerCase().includes('operating system') || addon.name.toLowerCase().includes('fedora') || addon.name.toLowerCase().includes('ubuntu') || addon.name.toLowerCase().includes('centos') || addon.name.toLowerCase().includes('debian') || addon.name.toLowerCase().includes('windows') || addon.name.toLowerCase().includes('almalinux') || addon.name.toLowerCase().includes('rocky') || addon.name.toLowerCase().includes('freebsd') ? 'Operating System' : addon.input_name || addon.name,
          // Add billing cycle specific pricing
          [billingCycle]: addon[billingCycle] || '0.00',
          // Add setup fee
          [billingCycle[0] + 'setupfee']: addon[billingCycle[0] + 'setupfee'] || '0.00',
          // Add billing cycle info
          billingcycle: addon.billingcycle || 'recurring',
          // Add price for cart calculation
          price: parseFloat(addon[billingCycle] || 0),
          setupfee: parseFloat(addon[billingCycle[0] + 'setupfee'] || 0)
        };

        updatedAddons.push(addonWithPricing);
      }
    } else {
      // Remove addon (for radio buttons, this shouldn't happen, but keeping for safety)
      updatedAddons = updatedAddons.filter(selected => selected.id !== addon.id);
    }

    // Handle special cases - check for OS addons by name patterns too
    const isOSAddon = addon.input_name === 'Operating System' || 
                     addon.name.toLowerCase().includes('operating system') ||
                     addon.name.toLowerCase().includes('ubuntu') ||
                     addon.name.toLowerCase().includes('centos') ||
                     addon.name.toLowerCase().includes('debian') ||
                     addon.name.toLowerCase().includes('windows') ||
                     addon.name.toLowerCase().includes('fedora') ||
                     addon.name.toLowerCase().includes('almalinux') ||
                     addon.name.toLowerCase().includes('rocky') ||
                     addon.name.toLowerCase().includes('freebsd');
    
    if (isOSAddon) {
      setSelectedOperatingSystem(isSelected ? addon.name : '');

      // Remove control panel and support level if OS changes
      updatedAddons = updatedAddons.filter(selected =>
        !['Control Panel', 'Support Level'].includes(selected.input_name)
      );
    }
    // Update addons first
    if (onAddonsChange) {
      onAddonsChange(updatedAddons);
    }

    // Don't trigger validation here - let useEffect handle it
    // This prevents double validation and looping
  };

  const isAddonSelected = (addon) => {
    return selectedAddons.some(selected => selected.id === addon.id);
  };

  // Check if addon is already in existing addons (from cart)
  const isAddonInCart = (addon) => {
    return existingAddons.some(existing => existing.id === addon.id);
  };

  const getAddonPrice = (addon) => {
    const price = addon[billingCycle];
    const setupFee = addon[billingCycle[0] + 'setupfee'];

    if (price === '-1.00') return 'GRATIS';

    let priceText = goldenFastAPI.formatRupiah(price);
    if (setupFee && setupFee !== '0.00' && setupFee !== '-1.00') {
      priceText += ` + Setup ${goldenFastAPI.formatRupiah(setupFee)}`;
    }

    return priceText;
  };

  const shouldShowAddon = (addon, inputName) => {
    // Always show non-conditional addons and Support Level (no OS-based filtering)
    if (!['Control Panel'].includes(inputName)) {
      return true;
    }

    // Show control panels only if compatible with selected OS
    if (inputName === 'Control Panel') {
      return !selectedOperatingSystem ||
        availableControlPanels.some(cp => cp.id === addon.id);
    }

    return true;
  };

  const renderAddonOption = (addon, inputName) => {
    if (!shouldShowAddon(addon, inputName)) {
      return null;
    }

    const isSelected = isAddonSelected(addon);
    const price = getAddonPrice(addon);
    const isInCart = isAddonInCart(addon);
    
    // Prevent showing addon if it's already in cart and not selected
    if (isInCart && !isSelected) {
      return null;
    }

    // Check for duplicate addons with same name and value
    const isDuplicate = selectedAddons.some(selected => 
      selected.id !== addon.id && 
      selected.name === addon.name && 
      selected[billingCycle] === addon[billingCycle]
    );
    
    if (isDuplicate) {
      return null;
    }

    // Determine if this category allows multiple selections
    const allowMultiple = inputName.toLowerCase().includes('additional') ||
      inputName.toLowerCase().includes('extra') ||
      inputName.toLowerCase().includes('backup') ||
      inputName === 'Lainnya' || // Special case for "others" addons
      addon.input_name === 'others' ||
      addon.input_name === 'Others';

    return (
      <div className={`card addon-option ${isSelected ? 'border-primary' : 'border-light'} mb-2 ${isInCart ? 'border-success' : ''}`}>
        <div className="card-body p-3">
          <div className="form-check d-flex align-items-start">
            <input
              className="form-check-input mt-1 me-3"
              type={allowMultiple ? "checkbox" : "radio"}
              name={allowMultiple ? `addon-${addon.id}` : `addon-${inputName}`} // Group radio buttons by input_name
              id={`addon-${addon.id}`}
              checked={isSelected}
              onChange={(e) => handleAddonChange(addon, e.target.checked)}
            />
            <div className="flex-grow-1">
              <label className="form-check-label w-100 cursor-pointer" htmlFor={`addon-${addon.id}`}>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="fw-semibold text-dark mb-1 d-flex align-items-center">
                      {addon.name}
                      {isInCart && (
                        <span className="badge ms-2 small" style={{ backgroundColor: '#f8b038', color: '#000' }}>
                          <i className="fas fa-check me-1"></i>
                          Added
                        </span>
                      )}
                    </div>
                    {addon.description && (
                      <div className="text-muted small mb-2" style={{ lineHeight: '1.4' }}>
                        {addon.description}
                      </div>
                    )}
                    <div className="d-flex gap-2 flex-wrap">
                      <span className="badge bg-light text-dark small">
                        ID: {addon.id}
                      </span>
                      {addon.billingcycle && (
                        <span className="badge small" style={{ backgroundColor: '#f8b038', color: '#ffffff' }}>
                          {addon.billingcycle}
                        </span>
                      )}
                      {addon.category && (
                        <span className="badge bg-outline-secondary small">
                          {addon.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ms-3 text-end">
                    <Badge
                      bg="warning"
                      text="dark"
                      className="fs-6"
                      style={{ backgroundColor: '#f8b038 !important', color: '#000 !important' }}
                    >
                      {price}
                    </Badge>
                    {addon[billingCycle[0] + 'setupfee'] &&
                      addon[billingCycle[0] + 'setupfee'] !== '0.00' &&
                      addon[billingCycle[0] + 'setupfee'] !== '-1.00' && (
                        <div className="text-muted small mt-1">
                          Setup: {goldenFastAPI.formatRupiah(addon[billingCycle[0] + 'setupfee'])}
                        </div>
                      )}
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInputGroup = (inputAddons, inputName) => {
    // Sort addons by numeric values in name or by order
    const sortedAddons = [...inputAddons].sort((a, b) => {
      // First sort by order if available
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }

      // Then by price (free items first)
      const aPrice = parseFloat(a[billingCycle] || 0);
      const bPrice = parseFloat(b[billingCycle] || 0);
      const aIsFree = aPrice <= 0 || a[billingCycle] === '-1.00';
      const bIsFree = bPrice <= 0 || b[billingCycle] === '-1.00';

      if (aIsFree && !bIsFree) return -1;
      if (!aIsFree && bIsFree) return 1;

      // Then by numeric values in name
      const aNum = a.name.match(/\d+/);
      const bNum = b.name.match(/\d+/);
      const aNumber = aNum ? parseInt(aNum[0]) : 0;
      const bNumber = bNum ? parseInt(bNum[0]) : 0;

      if (aNumber !== bNumber) {
        return aNumber - bNumber;
      }

      // Finally by name alphabetically
      return a.name.localeCompare(b.name);
    });

    // Determine if this category allows multiple selections
    const allowMultiple = inputName.toLowerCase().includes('additional') ||
      inputName.toLowerCase().includes('extra') ||
      inputName.toLowerCase().includes('backup') ||
      inputName === 'Lainnya'; // Special case for "others" addons

    return (
      <div className="mb-4">
        <div className="sub-category-accordion">
          <div
            className="sub-category-header d-flex align-items-center justify-content-between p-3 bg-light rounded cursor-pointer"
            onClick={() => toggleSubCategory(inputName)}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center">
              <h6 className="fw-bold text-secondary mb-0 me-2">{inputName}</h6>
              {inputName.includes('Additional') && (
                <FontAwesomeIcon icon={faPlus} className="text-primary me-2" style={{ fontSize: '12px' }} />
              )}
              {inputName === 'Lainnya' && (
                <span className="text-warning me-2" style={{ fontSize: '14px' }}>🔧</span>
              )}
              <span className="badge bg-light text-dark small">
                {sortedAddons.length}
              </span>
            </div>
            <div className="text-primary small">
              <FontAwesomeIcon
                icon={faChevronRight}
                className={`chevron-icon ${expandedSubCategories.has(inputName) ? 'expanded' : ''}`}
              />
              {/* <span className="ms-1">
                Klik untuk {expandedSubCategories.has(inputName) ? 'sembunyikan' : 'lihat'}
              </span> */}
            </div>
          </div>

          {expandedSubCategories.has(inputName) && (
            <div className="sub-category-content p-3 border border-top-0 rounded-bottom">
              {/* Dynamic explanation based on selection type */}
              <div className="alert alert-info small mb-3 border-0 sub-category-explanation">
                <i className="fas fa-info-circle me-2"></i>
                {allowMultiple
                  ? inputName === 'Lainnya'
                    ? 'Pilih satu atau lebih layanan tambahan. Anda dapat memilih beberapa addon sekaligus.'
                    : `Pilih satu atau lebih opsi dari ${inputName.toLowerCase()}. Anda dapat memilih beberapa addon sekaligus.`
                  : `Pilih salah satu opsi dari ${inputName.toLowerCase()}. Hanya satu pilihan yang diperbolehkan per kategori.`
                }
              </div>

              <div className="row">
                {sortedAddons.map((addon, index) => (
                  <div key={`addon-option-${addon.id}-${inputName}-${index}`} className="col-12 mb-2">
                    {renderAddonOption(addon, inputName)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCategory = (categoryAddons, categoryName) => {
    if (!categoryAddons || categoryName === 'hidden') {
      return null;
    }

    // Get category display info
    const getCategoryInfo = (name) => {
      const categoryMap = {
        'Hardware': { icon: '🔧', color: '#f39c12', title: 'Hardware' },
        'Software': { icon: '💿', color: '#3498db', title: 'Software' },
        'Network': { icon: '🌐', color: '#27ae60', title: 'Network' },
        'Cross Connect': { icon: '🔗', color: '#e74c3c', title: 'Cross Connect' },
        'Backup': { icon: '💾', color: '#9b59b6', title: 'Backup' },
        'Support': { icon: '🛟', color: '#f1c40f', title: 'Support Level' },
        'Others': { icon: '⚙️', color: '#95a5a6', title: 'Others' }
      };

      return categoryMap[name] || { icon: '📦', color: '#7f8c8d', title: name };
    };

    const categoryInfo = getCategoryInfo(categoryName);
    const inputNames = Object.keys(categoryAddons);

    // Sort input names by order (if available)
    const sortedInputNames = inputNames.sort((a, b) => {
      const aOrder = categoryAddons[a][0]?.order || 0;
      const bOrder = categoryAddons[b][0]?.order || 0;
      return aOrder - bOrder;
    });

    // Count total addons in this category
    const totalAddons = inputNames.reduce((count, inputName) =>
      count + categoryAddons[inputName].length, 0
    );

    const isExpanded = expandedCategories.has(categoryName);

    return (
      <div key={categoryName} className="card mb-3">
        <div
          className={`card-header bg-white border-bottom cursor-pointer ${isExpanded ? 'expanded' : ''}`}
          onClick={() => toggleCategory(categoryName)}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
              <span
                className="me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: categoryInfo.color + '20',
                  fontSize: '18px'
                }}
              >
                {categoryInfo.icon}
              </span>
              <div>
                <strong style={{ color: categoryInfo.color }}>
                  {categoryInfo.title}
                </strong>
                <div className="text-muted small">
                  {totalAddons} addon{totalAddons !== 1 ? 's' : ''} tersedia
                </div>
              </div>
            </div>
            <div className="text-end">
              <div className="text-primary small">
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className={`me-1 chevron-icon ${isExpanded ? 'expanded' : ''}`}
                />
              </div>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="card-body">
            {sortedInputNames.map(inputName => (
              <div key={inputName}>
                {renderInputGroup(categoryAddons[inputName], inputName)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const validateRequiredConfigurationsWithAddons = (addonsToValidate) => {
    // Clear any existing timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    // Set a new timeout for validation with debouncing
    const timeoutId = setTimeout(() => {
      const errors = [];

      // Validation logic without debug logging

      // Check for required custom fields
      if (productDetail?.customfields?.customfield) {
        productDetail.customfields.customfield.forEach(field => {
          if (field.required === '*' && field.name.toLowerCase().includes('operating system')) {
            // Enhanced OS detection - check both input_name and name patterns
            const hasOSSelected = addonsToValidate.some(addon => {
              const isOS = addon.input_name === 'Operating System' || 
                         addon.name.toLowerCase().includes('operating system') ||
                         addon.name.toLowerCase().includes('ubuntu') ||
                         addon.name.toLowerCase().includes('centos') ||
                         addon.name.toLowerCase().includes('debian') ||
                         addon.name.toLowerCase().includes('windows') ||
                         addon.name.toLowerCase().includes('fedora') ||
                         addon.name.toLowerCase().includes('almalinux') ||
                         addon.name.toLowerCase().includes('rocky') ||
                         addon.name.toLowerCase().includes('freebsd');
              
              return isOS;
            });

            if (!hasOSSelected) {
              errors.push(`Operating System harus dipilih dari addon "Software"`);
            }
          }
        });
      } else {
        // Fallback: if no custom fields are defined, check if we have any OS addons selected
        // This handles cases where the product doesn't have custom fields but still needs OS validation
        const hasAnyOSAddon = addonsToValidate.some(addon => {
          return addon.input_name === 'Operating System' || 
                 addon.name.toLowerCase().includes('operating system') ||
                 addon.name.toLowerCase().includes('ubuntu') ||
                 addon.name.toLowerCase().includes('centos') ||
                 addon.name.toLowerCase().includes('debian') ||
                 addon.name.toLowerCase().includes('windows') ||
                 addon.name.toLowerCase().includes('fedora') ||
                 addon.name.toLowerCase().includes('almalinux') ||
                 addon.name.toLowerCase().includes('rocky') ||
                 addon.name.toLowerCase().includes('freebsd');
        });
        
        // Only add error if we have addons but no OS selected
        if (addonsToValidate.length > 0 && !hasAnyOSAddon) {
          errors.push(`Operating System harus dipilih dari addon "Software"`);
        }
      }

      // Validation completed

      // Notify parent component about validation status
      if (onValidationChange) {
        onValidationChange(errors.length === 0, errors);
      }
    }, 300); // Increased timeout to prevent rapid validation

    setValidationTimeout(timeoutId);
  };

  // Calculate total sub-categories for global controls
  const totalSubCategories = Object.values(addons).reduce((total, categoryAddons) => {
    return total + Object.keys(categoryAddons).length;
  }, 0);

  // Global expand/collapse functions
  const toggleAllCategories = () => {
    if (expandedCategories.size === Object.keys(addons).length) {
      setExpandedCategories(new Set());
      setExpandedSubCategories(new Set());
    } else {
      setExpandedCategories(new Set(Object.keys(addons)));
      // Auto-expand first sub-category of each category
      const allSubCategories = new Set();
      Object.values(addons).forEach(categoryAddons => {
        Object.keys(categoryAddons).forEach(subCategory => {
          allSubCategories.add(subCategory);
        });
      });
      setExpandedSubCategories(allSubCategories);
    }
  };

  const toggleAllSubCategories = () => {
    if (expandedSubCategories.size === totalSubCategories) {
      setExpandedSubCategories(new Set());
    } else {
      const allSubCategories = new Set();
      Object.values(addons).forEach(categoryAddons => {
        Object.keys(categoryAddons).forEach(subCategory => {
          allSubCategories.add(subCategory);
        });
      });
      setExpandedSubCategories(allSubCategories);
    }
  };

  if (!productDetail) {
    return (
      <div className="card rounded-4 border-0 shadow-sm mb-4">
        <div className="card-body">
          <h5 className="fw-bold text-qw-primary mb-3">Konfigurasi Addon</h5>
          <Alert variant="info">
            Pilih produk terlebih dahulu untuk melihat addon yang tersedia.
          </Alert>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card rounded-4 border-0 shadow-sm mb-4">
        <div className="card-body">
          <h5 className="fw-bold text-qw-primary mb-3">Konfigurasi Addon</h5>
          <div className="loading-spinner">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2 text-muted">Memuat addon...</div>
          </div>
        </div>
      </div>
    );
  }

  const renderCustomFields = () => {
    if (!productDetail?.customfields?.customfield || !Array.isArray(productDetail.customfields.customfield)) {
      return null;
    }

    return (
      <div className="mb-4">
        <h6 className="fw-bold text-secondary mb-3">📋 Konfigurasi Wajib Addon</h6>
        {productDetail.customfields.customfield.map(field => (
          <div key={field.id} className="mb-3">
            <label className="form-label fw-semibold">
              {field.name}
              {field.required === '*' && <span className="text-danger ms-1">*</span>}
            </label>
            {field.description && (
              <div className="text-muted small mb-2">{field.description}</div>
            )}
            {field.name.toLowerCase().includes('operating system') ? (
              <div className="text-muted small mb-2">
                Pilih Operating System dari addon "Software" di bawah ini
              </div>
            ) : (
              <input
                type="text"
                className="form-control"
                placeholder={`Masukkan ${field.name.toLowerCase()}`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="card rounded-4 border-0 shadow-sm mb-4">
      <div className="card-body">
        <h5 className="fw-bold text-qw-primary mb-3">Konfigurasi Addon</h5>

        {/* Custom Fields (Required Configuration) */}
        {renderCustomFields()}

        {Object.keys(addons).length === 0 ? (
          <Alert variant="info">
            Tidak ada addon tersedia untuk produk ini.
          </Alert>
        ) : (
          <>
            {/* Accordion Controls */}
            {/* Global expand/collapse controls */}
            <div className="global-controls">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={toggleAllCategories}
              >
                {expandedCategories.size === Object.keys(addons).length
                  ? 'Tutup Semua Kategori'
                  : 'Buka Semua Kategori'
                }
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={toggleAllSubCategories}
              >
                {expandedSubCategories.size === totalSubCategories
                  ? 'Tutup Semua Sub-Kategori'
                  : 'Buka Semua Sub-Kategori'
                }
              </button>
            </div>

            <div className="accordion-container">
              {Object.keys(addons).map(categoryName =>
                renderCategory(addons[categoryName], categoryName)
              )}
            </div>

            {/* Selected Addons Summary */}
            {(selectedAddons.length > 0 || existingAddons.length > 0) && (
              <div className="addon-summary mt-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light border-0">
                    <h6 className="fw-bold mb-0">
                      <i className="fas fa-list-check me-2"></i>
                      Ringkasan Addon Dipilih
                    </h6>
                  </div>
                  <div className="card-body">
                    {/* Existing addons from cart */}
                    {existingAddons.length > 0 && (
                      <div className="mb-3">
                        <div className="text-muted small mb-2">
                          <i className="fas fa-check-circle text-success me-1"></i>
                          Sudah di keranjang ({existingAddons.length}):
                        </div>
                        <ul className="list-unstyled mb-0">
                          {existingAddons.map(addon => (
                            <li key={`existing-addon-${addon.id}`} className="small mb-1">
                              <span className="text-success me-2">✓</span>
                              <span className="fw-medium">{addon.name}</span>
                              <span className="text-muted ms-2">({getAddonPrice(addon)})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* New addons to be added */}
                    {(() => {
                      const newAddons = selectedAddons.filter(addon => !existingAddons.some(existing => existing.id === addon.id));
                      return newAddons.length > 0 && (
                        <div>
                          <div className="text-muted small mb-2">
                            <i className="fas fa-plus-circle text-primary me-1"></i>
                            Akan ditambahkan ({newAddons.length}):
                          </div>
                          <ul className="list-unstyled mb-0">
                            {newAddons.map(addon => (
                              <li key={`selected-addon-${addon.id}`} className="small mb-1">
                                <span className="text-primary me-2">+</span>
                                <span className="fw-medium">{addon.name}</span>
                                <span className="text-muted ms-2">({getAddonPrice(addon)})</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VpsAddonsSelector; 